import { createAsyncThunk } from '@reduxjs/toolkit';
import { EApiStatuses } from '../../../constants';
import { IError } from '../../../models/inner-models';
import {EPlatform, EPlatformNames, EUser} from '../../constants';
import { addPlatformIdentifier } from '../../functions';
import instance from '../../middlewares/api';
import {
  IDeletePayload,
  IFetchShopifyType,
} from "../../../models/models";

const getShopifyLoginUrl = createAsyncThunk(
  addPlatformIdentifier(EPlatform.addPlatformUser, EPlatformNames.shopify),
  async (shopDomain: string, thunkAPI) => {
    try {
      const resp = await instance.post('shopify-user/add-shop',
        {
          shopDomain: shopDomain
        });

      return (!resp?.data || resp.data.status !== EApiStatuses.SUCCESS) ?
        thunkAPI.rejectWithValue({error: resp.data.message, code: resp.data.code}) :
        resp?.data;
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

const getShopifyBuyUrl = createAsyncThunk(
  addPlatformIdentifier(EPlatform.fetchPlatformPlaces, EPlatformNames.shopify),
  async (planId: string, thunkAPI) => {
    try {
      const resp = await instance.post('shopify-user/create-subscription',
        {
          planId
        });

      return (!resp?.data || resp.data.status !== EApiStatuses.SUCCESS) ?
        thunkAPI.rejectWithValue({error: resp.data.message, code: resp.data.code}) :
        resp?.data.result;
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

const fetchShopifyUser = createAsyncThunk(
  addPlatformIdentifier(EPlatform.fetchPlatformUsers, EPlatformNames.shopify),
  async (_, thunkAPI) => {
    try {
      const resp = await instance.get('shopify/get-shop');

      return (!resp?.data || resp.data.status !== EApiStatuses.SUCCESS || !resp?.data?.result) ?
        undefined : [{...resp.data.result}];
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

const deleteShop = createAsyncThunk(
  addPlatformIdentifier(EPlatform.deleteUser, EPlatformNames.shopify),
  async (_, thunkAPI) => {
    try {
      const data = await instance.post('shopify/delete-shop', {});

      return (!data?.data || data.data.status !== EApiStatuses.SUCCESS) ?
        thunkAPI.rejectWithValue({ error: data.data.message }) :
        data.data.result;
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

const sendConfirmShopify = createAsyncThunk(
  addPlatformIdentifier(EPlatform.sendConfirmPlatformUser, EPlatformNames.github),
  async (_, thunkAPI) => {
    try {
      const resp = await instance.post('shopify/send-confirm-shopify-user', {});
      return (!resp?.data || resp.data.status !== EApiStatuses.SUCCESS) ?
        thunkAPI.rejectWithValue({error: resp.data.message, ...resp.data}) :
        resp?.data.message;
    } catch (err) {
      const error = err as IError;

      return thunkAPI.rejectWithValue({error: error.message});
    }
  },
);

const confirmShopify = createAsyncThunk(
  addPlatformIdentifier(EPlatform.confirmPlatformUser, EPlatformNames.github),
  async (code: string, thunkAPI) => {
    try {
      const resp = await instance.post('shopify/confirm-user', {code});
      return (!resp?.data || resp.data.status !== EApiStatuses.SUCCESS) ?
        thunkAPI.rejectWithValue({error: resp.data.message}) :
        resp?.data.message;
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue({error: error.message});
    }
  },
);

const fetchShopifyAll = createAsyncThunk(
  addPlatformIdentifier(EPlatform.fetchAllItems, EPlatformNames.shopify),
  async (_, thunkAPI) => {
    try {
      const resp = await instance.get(`shopify/all`);
      return (!resp?.data || resp.data.status !== EApiStatuses.SUCCESS) ?
        thunkAPI.rejectWithValue({error: resp.data.message, code: resp.data.code}) :
        resp?.data?.result;
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

const fetchShopifyEntity = createAsyncThunk(
  addPlatformIdentifier(EPlatform.fetchUserItems, EPlatformNames.shopify),
  async (payload: IFetchShopifyType, thunkAPI) => {
    try {
      const resp = await instance.get(`shopify/${payload.entity}?page=${payload.page}&perPage=${payload.perPage}${payload.search ? `&search=${encodeURIComponent(payload.search)}` : ''}`, {
        //workspaceVersionId: pageVerId
      });
      return resp?.data?.result || [];
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

const backupShopifyItem = createAsyncThunk(
  addPlatformIdentifier(EPlatform.backupItem, EPlatformNames.shopify),
  async (_, thunkAPI) => {
    try {
      const resp = await instance.get(`shopify/backup-now`);
      return (!resp?.data || resp.data.status !== EApiStatuses.SUCCESS) ?
        thunkAPI.rejectWithValue({ error: resp.data.message }) : resp.data;
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

const deleteShopifyItem = createAsyncThunk(
  addPlatformIdentifier(EPlatform.deleteItem, EPlatformNames.shopify),
  async (payload: IDeletePayload, thunkAPI) => {
    try {
      console.log('payload',payload)
      const resp = await instance.delete('shopify/delete-workspaces',{
        data: payload
      });
      return (!resp?.data || resp.data.status !== EApiStatuses.SUCCESS) ?
        thunkAPI.rejectWithValue({ error: resp.data.message }) : resp.data;
    } catch (err) {
      const error = err as IError;
      return thunkAPI.rejectWithValue({ error: error.message });
    }
  },
);

export {
  getShopifyLoginUrl,
  sendConfirmShopify,
  confirmShopify,
  fetchShopifyUser,
  fetchShopifyAll,
  fetchShopifyEntity,
  backupShopifyItem,
  deleteShopifyItem,
  deleteShop,
  getShopifyBuyUrl
};
